/**
Copyright (c) 2022-2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlayIcon,
  StopIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  CommandLineIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PlusIcon,
  XMarkIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import GPUUnavailableModal from '../components/pods/GPUUnavailableModal';
import RelaunchPodModal, { isOOMFailure } from '../components/pods/RelaunchPodModal';
import ContainerLogs from '../components/pods/ContainerLogs';
import ContainerStats from '../components/pods/ContainerStats';
import WebTerminal from '../components/pods/WebTerminal';
import { containerService, waitlistService } from '../services/api';
import { Container, ContainerPort } from '../types';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { AxiosError } from 'axios';
import InsufficientBalanceModal from '../components/wallet/InsufficientBalanceModal';
import { useWallet } from '../hooks/useWallet';
import { useContainerEvents } from '../hooks/useContainerEvents';
import { Page, PageHeader, Card, Button, LinkButton, Tag, StatusTag, SectionHeader, EmptyState, KV, Skeleton } from '../components/console/ui';
import { TerminalIc, KeyIc, ExternalIc } from '../components/console/icons';
import Dialog, { Callout } from '../components/pods/Dialog';


const PodDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showGPUUnavailable, setShowGPUUnavailable] = useState(false);
  const [unavailableGPUType, setUnavailableGPUType] = useState<string>('');
  const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);
  const [showInsufficientBalance, setShowInsufficientBalance] = useState(false);
  const [balanceErrorMessage, setBalanceErrorMessage] = useState('');
  const [showEditPorts, setShowEditPorts] = useState(false);
  const [editingPorts, setEditingPorts] = useState<ContainerPort[]>([]);
  const [newPort, setNewPort] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showRelaunchModal, setShowRelaunchModal] = useState(false);
  const [showTerminal, setShowTerminal] = useState(false);
  const [showSSHConfigModal, setShowSSHConfigModal] = useState(false);
  const [sshConfigTab, setSSHConfigTab] = useState<'linux' | 'mac' | 'powershell' | 'gitbash'>('mac');
  const [copiedConfig, setCopiedConfig] = useState(false);

  // Auto-detect OS on mount
  React.useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('win')) {
      setSSHConfigTab('powershell');
    } else if (userAgent.includes('mac')) {
      setSSHConfigTab('mac');
    } else {
      setSSHConfigTab('linux');
    }
  }, []);

  // Wallet data for balance check
  const { availableBalance, minimumRequired } = useWallet();

  // Read cached container data to derive the project ID before calling useQuery.
  // On first render this is undefined (cache miss) → containerProjectId is null → hook no-ops.
  // After the first successful fetch React re-renders with the real project ID.
  const cachedContainerData = queryClient.getQueryData<any>(['container', id]);
  const containerProjectId =
    cachedContainerData?.container?.project_id ??
    cachedContainerData?.project_id ??
    null;

  // Subscribe to realtime events for this container's own project.
  // Passing null is safe - the hook is a no-op when projectId is falsy.
  const { connected: realtimeConnected } = useContainerEvents(containerProjectId);
  // Only relax polling when the socket is connected for THIS container's project.
  // realtimeConnected reflects the singleton state which may briefly be true from
  // a previous page's socket until the new effect fires; containerProjectId guards this.
  const effectiveRealtime = realtimeConnected && !!containerProjectId;

  const { data: containerData, isLoading, error } = useQuery({
    queryKey: ['container', id],
    queryFn: async () => {
      // First get user's containers to find the project_id
      const listRes = await containerService.getMyContainers();
      const responseData = listRes.data;
      // Handle the response format: {containers: [...], ...} or direct array
      const containers = responseData?.containers || (Array.isArray(responseData) ? responseData : []);
      const container = containers.find((c: Container) => c.id === id);
      if (!container) throw new Error('Container not found');

      // Now get the full details with project_id header
      const res = await containerService.get(id!, container.project_id);
      return res.data;
    },
    enabled: !!id,
    // Realtime socket pushes status changes; polling is only the fallback.
    refetchInterval: effectiveRealtime ? 30000 : 10000,
  });

  const container: Container | undefined = containerData?.container || containerData;

  // Parse ports from JSON string
  const parsedPorts = useMemo((): ContainerPort[] => {
    if (!container?.ports) return [];
    try {
      const ports = JSON.parse(container.ports);
      // Handle both structured format [{container_port: 8080}] and simple format [8080]
      if (Array.isArray(ports)) {
        return ports.map((p: ContainerPort | number) => {
          if (typeof p === 'number') {
            return { container_port: p, protocol: 'TCP', name: `tcp-${p}` };
          }
          return p;
        });
      }
      return [];
    } catch {
      return [];
    }
  }, [container?.ports]);

  // Get endpoint URL for a port
  // SSH: ssh-<subdomain>.cloud.podstack.ai
  // HTTP: <subdomain>-<port>.cloud.podstack.ai
  const getPortEndpoint = (port: number): string => {
    if (!container?.ssh_subdomain) return '';
    if (port === 22) {
      // SSH endpoint: ssh-<subdomain>.cloud.podstack.ai
      return `ssh-${container.ssh_subdomain}.cloud.podstack.ai`;
    }
    // HTTP endpoint: <subdomain>-<port>.cloud.podstack.ai
    return `https://${container.ssh_subdomain}-${port}.cloud.podstack.ai`;
  };

  // Get SSH command for display
  const getSSHCommand = (): string | null => {
    if (!container?.ssh_subdomain) return null;
    return `ssh -i ~/.ssh/<key_name> podstack@ssh-${container.ssh_subdomain}.cloud.podstack.ai`;
  };

  // Update container mutation
  const updateMutation = useMutation({
    mutationFn: (ports: ContainerPort[]) =>
      containerService.update(id!, container?.project_id || '', { ports }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['container', id] });
      queryClient.invalidateQueries({ queryKey: ['containers'] });
      toast.success('Ports updated successfully');
      setShowEditPorts(false);
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to update ports');
    },
  });

  // Port editing functions
  const openEditPorts = () => {
    setEditingPorts([...parsedPorts]);
    setNewPort('');
    setShowEditPorts(true);
  };

  const addPort = () => {
    const portNum = parseInt(newPort, 10);
    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
      toast.error('Please enter a valid port number (1-65535)');
      return;
    }
    if (editingPorts.some(p => p.container_port === portNum)) {
      toast.error('Port already exists');
      return;
    }
    setEditingPorts([...editingPorts, {
      container_port: portNum,
      protocol: 'TCP',
      name: `tcp-${portNum}`,
    }]);
    setNewPort('');
  };

  const removePort = (portNum: number) => {
    // Don't allow removing port 22 if SSH is enabled
    if (portNum === 22 && container?.ssh_subdomain) {
      toast.error('Cannot remove SSH port while SSH access is enabled');
      return;
    }
    setEditingPorts(editingPorts.filter(p => p.container_port !== portNum));
  };

  const savePorts = () => {
    updateMutation.mutate(editingPorts);
  };

  const startMutation = useMutation({
    mutationFn: (opts: { memoryGb?: number } | void) =>
      containerService.start(
        id!,
        container?.project_id || '',
        opts?.memoryGb ? { memory_gb: opts.memoryGb } : undefined,
      ),
    onSuccess: () => {
      setShowRelaunchModal(false);
      queryClient.invalidateQueries({ queryKey: ['container', id] });
      queryClient.invalidateQueries({ queryKey: ['containers'] });
      toast.success(container?.status?.toLowerCase() === 'failed' ? 'Pod relaunching...' : 'Pod starting...');
    },
    onError: (error: AxiosError<{ error?: string; code?: string; gpu_type?: string; can_join_waitlist?: boolean; message?: string }>) => {
      // Check if this is an insufficient balance error (HTTP 402)
      if (error.response?.status === 402 || error.response?.data?.code === 'insufficient_balance') {
        setBalanceErrorMessage(error.response?.data?.message || 'Insufficient wallet balance to start this pod.');
        setShowInsufficientBalance(true);
        return;
      }
      // Check if this is a GPU unavailable error
      if (
        error.response?.status === 409 &&
        error.response?.data?.code === 'GPU_UNAVAILABLE' &&
        error.response?.data?.can_join_waitlist
      ) {
        setUnavailableGPUType(error.response.data.gpu_type || container?.gpu_type || 'GPU');
        setShowGPUUnavailable(true);
        return;
      }
      toast.error(error.response?.data?.error || 'Failed to start pod');
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => containerService.stop(id!, container?.project_id || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['container', id] });
      queryClient.invalidateQueries({ queryKey: ['containers'] });
      toast.success('Pod stopping...');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => containerService.delete(id!, container?.project_id || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['containers'] });
      toast.success('Pod deleted');
      navigate('/pods');
    },
  });

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  // OOM kills get a confirm that suggests a memory bump; other failures relaunch directly.
  const handleRelaunch = () => {
    if (isOOMFailure(container?.failure_reason)) {
      setShowRelaunchModal(true);
    } else {
      startMutation.mutate();
    }
  };

  const confirmDelete = () => {
    deleteMutation.mutate();
    setShowDeleteConfirm(false);
  };

  const handleJoinWaitlist = async () => {
    if (!container) return;
    setIsJoiningWaitlist(true);
    try {
      const response = await waitlistService.join({
        gpu_type: unavailableGPUType || container.gpu_type || '',
        gpu_count: container.gpu_count || 1,
        project_id: container.project_id,
      });
      const queuePosition = response.data?.queue_position || 1;
      const totalInQueue = response.data?.total_in_queue || 1;
      toast.success(`Added to waitlist for ${unavailableGPUType}! Queue position: #${queuePosition} of ${totalInQueue}. Check your email for confirmation.`);
      setShowGPUUnavailable(false);
    } catch (err) {
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setIsJoiningWaitlist(false);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const copyBtn = (text: string, field: string) => (
    <button
      type="button"
      onClick={() => copyToClipboard(text, field)}
      className="w-10 h-10 flex-none grid place-items-center rounded-md text-ps-muted hover:text-ps-accent hover:bg-ps-hover"
      title="Copy to clipboard"
      aria-label="Copy to clipboard"
    >
      {copiedField === field ? <CheckIcon className="w-5 h-5 text-ps-ok" /> : <ClipboardDocumentIcon className="w-5 h-5" />}
    </button>
  );

  // Get notebook URL - uses the first HTTP port's subdomain
  const getNotebookUrl = () => {
    if (!container?.ssh_subdomain) return null;
    // Find the first non-SSH port for notebook access
    const httpPorts = parsedPorts.filter(p => p.container_port !== 22);
    if (httpPorts.length === 0) return null;
    const firstPort = httpPorts[0].container_port;
    return `https://${container.ssh_subdomain}-${firstPort}.cloud.podstack.ai`;
  };

  const status = container?.status?.toLowerCase();
  const hasGpu = !!container && (container.gpu_count > 0 || (!!container.gpu_fraction && container.gpu_fraction > 0));

  if (isLoading) {
    return (
      <Page>
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 lg:col-span-2" />
          <Skeleton className="h-64" />
        </div>
      </Page>
    );
  }

  if (error || !container) {
    return (
      <Page>
        <PageHeader back={{ to: '/pods', label: 'GPU Pods' }} title="Pod not found" />
        <EmptyState
          icon={<ExclamationTriangleIcon />}
          title="Pod not found"
          body="The pod you're looking for doesn't exist or has been deleted."
          actions={<LinkButton to="/pods">Back to GPU Pods</LinkButton>}
        />
      </Page>
    );
  }

  const spinner = <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />;

  return (
    <Page wide>
      <PageHeader
        back={{ to: '/pods', label: 'GPU Pods' }}
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="break-all">{container.name}</span>
            <StatusTag status={container.status} />
          </span>
        }
        subtitle={
          <span className="font-mono text-sm">
            {container.id.slice(0, 8)} · {container.gpu_type ? `${container.gpu_type} · ` : ''}created{' '}
            {format(new Date(container.created_at), 'd MMM yyyy, HH:mm')}
          </span>
        }
        actions={
          <>
            {status === 'running' ? (
              <Button variant="secondary" onClick={() => setShowStopConfirm(true)} disabled={stopMutation.isPending}>
                {stopMutation.isPending ? spinner : <StopIcon className="w-4 h-4" />}
                {stopMutation.isPending ? 'Stopping...' : 'Stop'}
              </Button>
            ) : status === 'stopped' ? (
              <Button onClick={() => startMutation.mutate()} disabled={startMutation.isPending}>
                {startMutation.isPending ? spinner : <PlayIcon className="w-4 h-4" />}
                {startMutation.isPending ? 'Starting...' : 'Start'}
              </Button>
            ) : status === 'failed' ? (
              <Button onClick={handleRelaunch} disabled={startMutation.isPending}>
                {startMutation.isPending ? spinner : <PlayIcon className="w-4 h-4" />}
                {startMutation.isPending ? 'Relaunching...' : 'Relaunch'}
              </Button>
            ) : null}
            <Button variant="danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
              <TrashIcon className="w-4 h-4" />
              Delete
            </Button>
          </>
        }
      />

      {/* Failure Banner */}
      {status === 'failed' && (
        <div className="flex flex-wrap items-start gap-3 p-4 rounded-[14px] border border-[#4A1F1F] bg-ps-err-bg">
          <ExclamationTriangleIcon className="w-5 h-5 text-ps-err flex-none mt-0.5" />
          <div className="min-w-0 flex-[1_1_280px]">
            <p className="m-0 text-sm font-semibold text-ps-err">Pod failed</p>
            <p className="m-0 mt-1 text-sm text-ps-err/80 break-words">
              {container.failure_reason || 'The pod stopped running unexpectedly.'}
            </p>
            <p className="m-0 mt-2 text-xs text-ps-muted">
              Its resources have been released and billing is paused. Relaunch to redeploy with the same configuration, or
              delete the pod.
            </p>
          </div>
          <Button onClick={handleRelaunch} disabled={startMutation.isPending}>
            {startMutation.isPending ? spinner : <PlayIcon className="w-4 h-4" />}
            {startMutation.isPending ? 'Relaunching...' : 'Relaunch'}
          </Button>
        </div>
      )}

      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {/* Main column */}
          <div className="lg:col-span-2 flex flex-col gap-6 min-w-0">
            {/* Overview */}
            <Card className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="min-w-0">
                  <p className="m-0 text-xs text-ps-subtle">Created</p>
                  <p className="m-0 mt-1 text-sm">{format(new Date(container.created_at), 'MMM d, yyyy HH:mm')}</p>
                </div>
                <div className="min-w-0">
                  <p className="m-0 text-xs text-ps-subtle">Image</p>
                  <p className="m-0 mt-1 text-sm font-mono truncate" title={container.image}>
                    {container.image.split('/').pop()?.split(':')[0] || container.image}
                  </p>
                </div>
                <div>
                  <p className="m-0 text-xs text-ps-subtle">Replicas</p>
                  <p className="m-0 mt-1 text-sm">{container.replicas}</p>
                </div>
                <div>
                  <p className="m-0 text-xs text-ps-subtle">Billing</p>
                  <p className="m-0 mt-1 text-sm capitalize">{container.billing_period}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 pt-5 border-t border-ps-border">
                <div className="rounded-lg bg-ps-chrome border border-ps-border p-3">
                  <p className="m-0 text-xs text-ps-subtle">CPU</p>
                  <p className="m-0 mt-1 text-lg font-semibold">{container.cpu_cores} vCPU</p>
                </div>
                <div className="rounded-lg bg-ps-chrome border border-ps-border p-3">
                  <p className="m-0 text-xs text-ps-subtle">Memory</p>
                  <p className="m-0 mt-1 text-lg font-semibold">{container.memory_gb} GB</p>
                </div>
                <div className="rounded-lg bg-ps-chrome border border-ps-border p-3">
                  <p className="m-0 text-xs text-ps-subtle">Storage</p>
                  <p className="m-0 mt-1 text-lg font-semibold">{container.storage_gb || 10} GB</p>
                </div>
                {hasGpu && (
                  <div className="rounded-lg bg-ps-chrome border border-ps-border p-3">
                    <p className="m-0 text-xs text-ps-subtle">GPU</p>
                    <p className="m-0 mt-1 text-lg font-semibold">
                      {container.gpu_fraction ? `${Math.round(container.gpu_fraction * 100)}%` : `${container.gpu_count}×`}
                    </p>
                    {container.gpu_type && <p className="m-0 text-xs text-ps-muted">{container.gpu_type}</p>}
                  </div>
                )}
              </div>
            </Card>

            {/* Web Terminal - all running pods */}
            {status === 'running' && (
              <Card className="p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <SectionHeader title={<span className="flex items-center gap-2"><TerminalIc />Web terminal</span>} />
                  <Button size="sm" variant={showTerminal ? 'secondary' : 'primary'} onClick={() => setShowTerminal(!showTerminal)}>
                    <CommandLineIcon className="w-4 h-4" />
                    {showTerminal ? 'Hide terminal' : 'Open terminal'}
                  </Button>
                </div>
                {!showTerminal ? (
                  <p className="m-0 text-sm text-ps-muted">
                    Access an interactive shell in your running container directly from the browser.
                  </p>
                ) : (
                  <WebTerminal
                    containerId={container.id}
                    projectId={container.project_id}
                    containerName={container.name}
                    isRunning={true}
                    onClose={() => setShowTerminal(false)}
                  />
                )}
              </Card>
            )}

            {/* SSH Access */}
            {container.ssh_subdomain && (
              <Card className="p-5 flex flex-col gap-3">
                <SectionHeader
                  title={<span className="flex items-center gap-2"><KeyIc />SSH access</span>}
                  action={
                    <button onClick={() => setShowSSHConfigModal(true)} className="ps-link">
                      Configure SSH
                    </button>
                  }
                />
                <div>
                  <span className="ps-label">SSH command</span>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 min-w-0 bg-ps-chrome border border-ps-border text-ps-ok px-3 py-2.5 rounded-md font-mono text-sm break-all">
                      {getSSHCommand()}
                    </code>
                    {copyBtn(getSSHCommand()!, 'ssh')}
                  </div>
                </div>
                <div className="text-sm text-ps-muted flex flex-col gap-1">
                  <p className="m-0">
                    Replace <code className="text-ps-warn">&lt;key_name&gt;</code> with your private key filename (e.g.{' '}
                    <code className="text-ps-warn">id_rsa</code> or <code className="text-ps-warn">id_ed25519</code>).
                  </p>
                  <p className="m-0">
                    Connect using your SSH key.{' '}
                    <button onClick={() => setShowSSHConfigModal(true)} className="ps-link">
                      First-time setup required
                    </button>
                  </p>
                </div>
              </Card>
            )}

            {/* Notebook Access */}
            {container.notebook_password && (
              <Card className="p-5 flex flex-col gap-3">
                <SectionHeader title={<span className="flex items-center gap-2"><GlobeAltIcon className="w-[18px] h-[18px]" />Notebook access</span>} />
                {container.ssh_subdomain && getNotebookUrl() && (
                  <div>
                    <span className="ps-label">Notebook URL</span>
                    <div className="flex items-center gap-2">
                      <a
                        href={getNotebookUrl()!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 min-w-0 bg-ps-chrome border border-ps-border text-ps-info px-3 py-2.5 rounded-md font-mono text-sm hover:underline break-all"
                      >
                        {getNotebookUrl()}
                      </a>
                      {copyBtn(getNotebookUrl()!, 'url')}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="ps-label">Username</span>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-ps-chrome border border-ps-border px-3 py-2.5 rounded-md font-mono text-sm">
                        {container.notebook_username || 'podstack'}
                      </code>
                      {copyBtn(container.notebook_username || 'podstack', 'username')}
                    </div>
                  </div>
                  <div>
                    <span className="ps-label">Password</span>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 min-w-0 bg-ps-chrome border border-ps-border px-3 py-2.5 rounded-md font-mono text-sm break-all">
                        {container.notebook_password}
                      </code>
                      {copyBtn(container.notebook_password!, 'password')}
                    </div>
                  </div>
                </div>
                <Callout tone="warn" title="Important">
                  Save these credentials securely. The password was auto-generated and won't be shown again after you leave this
                  page.
                </Callout>
              </Card>
            )}

            {/* Exposed Ports & Endpoints */}
            <Card className="p-5 flex flex-col gap-3">
              <SectionHeader
                title={<span className="flex items-center gap-2"><LinkIcon className="w-[18px] h-[18px]" />Exposed ports &amp; endpoints</span>}
                action={
                  <button onClick={openEditPorts} className="ps-link flex items-center gap-1">
                    <PencilIcon className="w-4 h-4" />
                    Edit
                  </button>
                }
              />
              {parsedPorts.length === 0 ? (
                <div className="rounded-lg border border-dashed border-ps-strong p-5 text-center text-sm text-ps-muted">
                  No ports exposed.{' '}
                  <button onClick={openEditPorts} className="ps-link">
                    + Add ports
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {parsedPorts.map((port) => (
                    <div key={port.container_port} className="rounded-lg border border-ps-border bg-ps-chrome p-3">
                      <div className="flex items-center gap-2">
                        <Tag tone={port.container_port === 22 ? 'ok' : 'info'}>{port.container_port === 22 ? 'SSH' : 'HTTP'}</Tag>
                        <span className="font-medium">Port {port.container_port}</span>
                        {port.protocol && <span className="text-xs text-ps-subtle">({port.protocol})</span>}
                      </div>
                      {container.ssh_subdomain && (
                        <div className="flex items-center gap-2 mt-2">
                          <code className="flex-1 min-w-0 text-xs bg-ps-page px-2 py-1.5 rounded border border-ps-border text-ps-muted font-mono truncate">
                            {getPortEndpoint(port.container_port)}
                          </code>
                          {copyBtn(getPortEndpoint(port.container_port), `port-${port.container_port}`)}
                          {port.container_port !== 22 && (
                            <a
                              href={getPortEndpoint(port.container_port)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-10 h-10 grid place-items-center rounded-md text-ps-muted hover:text-ps-accent hover:bg-ps-hover"
                              title="Open in new tab"
                              aria-label={`Open port ${port.container_port} in new tab`}
                            >
                              <ExternalIc className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!container.ssh_subdomain && parsedPorts.length > 0 && (
                <Callout tone="off">
                  Endpoints are being provisioned. If this persists, the pod may have been created before endpoint routing was
                  enabled.
                </Callout>
              )}
            </Card>

            {/* Container Logs */}
            <ContainerLogs
              containerId={container.id}
              projectId={container.project_id}
              containerName={container.name}
              isRunning={status === 'running'}
            />
          </div>

          {/* Sidebar */}
          <div className="flex flex-col gap-6 min-w-0">
            <Card className="p-5 flex flex-col gap-3">
              <h2 className="m-0 text-base font-medium">Billing</h2>
              <KV k="Period" v={<span className="capitalize">{container.billing_period}</span>} />
              <KV
                k="Status"
                v={
                  status === 'stopped' || container.replicas === 0 ? (
                    <Tag tone="off">Paused</Tag>
                  ) : container.billing_started ? (
                    <Tag tone="ok">Active</Tag>
                  ) : (
                    <Tag tone="warn">Not started</Tag>
                  )
                }
              />
              {container.total_billed_inr !== undefined && <KV k="Total billed" v={`₹${container.total_billed_inr.toFixed(2)}`} />}
              {container.last_billed_at && <KV k="Last billed" v={format(new Date(container.last_billed_at), 'MMM d, HH:mm')} />}
              {(container.cpu_rate_per_hour || container.gpu_rate_per_hour) && (
                <div className="border-t border-ps-border pt-3 flex flex-col gap-1.5">
                  <p className="m-0 ps-eyebrow !text-ps-subtle">Hourly rates</p>
                  {container.cpu_rate_per_hour && <KV k="CPU" v={`₹${container.cpu_rate_per_hour.toFixed(4)}/hr`} />}
                  {container.memory_rate_per_hour && <KV k="Memory" v={`₹${container.memory_rate_per_hour.toFixed(4)}/hr`} />}
                  {container.storage_rate_per_hour && <KV k="Storage" v={`₹${container.storage_rate_per_hour.toFixed(4)}/hr`} />}
                  {container.gpu_rate_per_hour && <KV k="GPU" v={`₹${container.gpu_rate_per_hour.toFixed(4)}/hr`} />}
                </div>
              )}
            </Card>

            {/* Infrastructure Stats */}
            <ContainerStats
              containerId={container.id}
              projectId={container.project_id}
              isRunning={status === 'running'}
              allocatedCpu={container.cpu_cores}
              allocatedMemoryGb={container.memory_gb}
              allocatedStorageGb={container.storage_gb || 10}
              gpuCount={container.gpu_count}
              gpuFraction={container.gpu_fraction}
              gpuType={container.gpu_type}
            />
          </div>
        </div>
      </div>

      <GPUUnavailableModal
        isOpen={showGPUUnavailable}
        onClose={() => setShowGPUUnavailable(false)}
        onJoinWaitlist={handleJoinWaitlist}
        gpuType={unavailableGPUType}
        containerName={container?.name}
        isJoining={isJoiningWaitlist}
      />

      {/* OOM Relaunch Modal - suggests a memory bump before redeploying */}
      <RelaunchPodModal
        isOpen={showRelaunchModal}
        pod={container || null}
        isPending={startMutation.isPending}
        onClose={() => setShowRelaunchModal(false)}
        onRelaunch={(memoryGb) => startMutation.mutate({ memoryGb })}
      />

      <InsufficientBalanceModal
        isOpen={showInsufficientBalance}
        onClose={() => setShowInsufficientBalance(false)}
        message={balanceErrorMessage}
        requiredAmount={minimumRequired}
        currentBalance={availableBalance}
      />

      {/* Edit Ports */}
      <Dialog
        open={showEditPorts}
        onClose={() => setShowEditPorts(false)}
        title="Edit exposed ports"
        footer={
          <>
            <button type="button" onClick={() => setShowEditPorts(false)} className="ps-btn2">
              Cancel
            </button>
            <button type="button" onClick={savePorts} disabled={updateMutation.isPending} className="ps-btn">
              {updateMutation.isPending ? 'Saving...' : 'Save changes'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              max="65535"
              value={newPort}
              onChange={(e) => setNewPort(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addPort()}
              placeholder="Port number (1-65535)"
              aria-label="Port number"
              className="ps-input flex-1"
            />
            <button type="button" onClick={addPort} className="ps-btn">
              <PlusIcon className="w-4 h-4" />
              Add
            </button>
          </div>
          <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
            {editingPorts.length === 0 ? (
              <p className="m-0 text-sm text-ps-muted text-center py-4">No ports added yet</p>
            ) : (
              editingPorts.map((port) => {
                const locked = port.container_port === 22 && !!container?.ssh_subdomain;
                return (
                  <div key={port.container_port} className="flex items-center justify-between gap-2 px-3 min-h-[44px] rounded-lg bg-ps-raised">
                    <div className="flex items-center gap-2">
                      <Tag tone={port.container_port === 22 ? 'ok' : 'info'}>{port.container_port === 22 ? 'SSH' : 'HTTP'}</Tag>
                      <span className="font-medium">Port {port.container_port}</span>
                      <span className="text-xs text-ps-subtle">({port.protocol || 'TCP'})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removePort(port.container_port)}
                      disabled={locked}
                      title={locked ? 'Cannot remove SSH port' : 'Remove port'}
                      aria-label={`Remove port ${port.container_port}`}
                      className={`w-9 h-9 grid place-items-center rounded ${locked ? 'text-ps-strong cursor-not-allowed' : 'text-ps-err hover:bg-ps-hover'}`}
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
          <Callout tone="off">
            Changes will be applied to the running pod.
            {container?.ssh_subdomain && ' SSH port (22) cannot be removed while SSH access is enabled.'}
          </Callout>
        </div>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete pod"
        footer={
          <>
            <button type="button" onClick={() => setShowDeleteConfirm(false)} className="ps-btn2">
              Cancel
            </button>
            <button type="button" onClick={confirmDelete} disabled={deleteMutation.isPending} className="ps-btn-danger">
              {deleteMutation.isPending ? 'Deleting...' : 'Delete pod'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-ps-muted">
            Are you sure you want to delete <span className="font-semibold text-ps-text">“{container?.name}”</span>?
          </p>
          <Callout tone="err" title="This action cannot be undone">
            <ul className="m-0 pl-4 list-disc">
              <li>All data stored in this pod will be permanently deleted</li>
              {status === 'running' && hasGpu && <li>The {container.gpu_type || 'GPU'} will be released back to the pool</li>}
            </ul>
          </Callout>
          <Callout tone="warn" title="Billing notice">
            No pro-rated refund. You will be billed for the complete {container?.billing_period || 'current'} billing cycle even
            if you delete now.
          </Callout>
        </div>
      </Dialog>

      {/* Stop Confirmation */}
      <Dialog
        open={showStopConfirm}
        onClose={() => setShowStopConfirm(false)}
        title="Stop pod"
        footer={
          <>
            <button type="button" onClick={() => setShowStopConfirm(false)} className="ps-btn2">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                stopMutation.mutate();
                setShowStopConfirm(false);
              }}
              disabled={stopMutation.isPending}
              className="ps-btn"
            >
              {stopMutation.isPending ? 'Stopping...' : 'Stop pod'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-ps-muted">
            Are you sure you want to stop <span className="font-semibold text-ps-text">“{container?.name}”</span>?
          </p>
          {hasGpu && (
            <Callout tone="warn" title="GPU will be released">
              The {container.gpu_type || 'GPU'} allocated to this pod will be released back to the pool. When you restart, the
              same GPU may not be available.
            </Callout>
          )}
          <Callout tone="warn" title="Data will be lost">
            Once the pod is stopped, any data not stored in NFS or other persistent storage will be permanently lost. Make sure
            important files are saved to persistent storage before stopping.
          </Callout>
          <Callout tone="off">Billing will be paused while the pod is stopped.</Callout>
        </div>
      </Dialog>

      {/* SSH Configuration Modal */}
      {showSSHConfigModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowSSHConfigModal(false)}
          />
          <div className="ps-card relative shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-ps-border">
              <div className="flex items-center gap-2">
                <TerminalIc className="w-5 h-5 text-ps-accent" />
                <h2 className="m-0 text-lg font-medium">SSH configuration</h2>
              </div>
              <button
                onClick={() => setShowSSHConfigModal(false)}
                aria-label="Close" className="w-10 h-10 grid place-items-center rounded-md text-ps-muted hover:text-ps-text hover:bg-ps-hover"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap border-b border-ps-border">
              {[
                { id: 'mac', label: 'macOS', icon: '🍎' },
                { id: 'linux', label: 'Linux/Unix', icon: '🐧' },
                { id: 'powershell', label: 'Windows PowerShell', icon: '🪟' },
                { id: 'gitbash', label: 'Windows Git Bash', icon: '🔧' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSSHConfigTab(tab.id as typeof sshConfigTab)}
                  className={`flex-1 min-w-[140px] min-h-[44px] px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    sshConfigTab === tab.id
                      ? 'text-ps-text border-ps-accent'
                      : 'text-ps-muted border-transparent hover:text-ps-text'
                  }`}
                >
                  <span className="mr-1">{tab.icon}</span> {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto flex-1">
              <div className="space-y-4">
                {/* Step 1: Add SSH Config */}
                <div>
                  <h4 className="text-sm font-semibold text-ps-text mb-2 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-ps-accent text-ps-chrome text-xs flex items-center justify-center">1</span>
                    Add SSH Configuration
                  </h4>
                  <p className="text-sm text-gray-400 mb-3">
                    {sshConfigTab === 'mac' || sshConfigTab === 'linux' ? (
                      <>Open your terminal and run the following command to add the SSH config:</>
                    ) : sshConfigTab === 'powershell' ? (
                      <>Open PowerShell and run the script below. It locates your OpenSSL install automatically - if you don't have it yet, do Step 2 first:</>
                    ) : (
                      <>Open Git Bash and run the following command:</>
                    )}
                  </p>

                  {/* Config file path info */}
                  <div className="bg-ps-raised rounded-lg p-3 mb-3">
                    <p className="text-xs text-gray-400 mb-1">Config file location:</p>
                    <code className="text-sm text-ps-warn font-mono">
                      {sshConfigTab === 'powershell'
                        ? 'C:\\Users\\<username>\\.ssh\\config'
                        : '~/.ssh/config'
                      }
                    </code>
                  </div>

                  {/* Command to append config */}
                  <div className="relative">
                    <pre className="bg-ps-page border border-ps-border text-ps-ok p-4 rounded-lg font-mono text-xs overflow-x-auto whitespace-pre-wrap">
{sshConfigTab === 'mac' || sshConfigTab === 'linux' ? `# Create .ssh directory if it doesn't exist
mkdir -p ~/.ssh && chmod 700 ~/.ssh

# Add the SSH config
cat >> ~/.ssh/config << 'EOF'

# Podstack SSH Configuration
Host *.cloud.podstack.ai
    ProxyCommand openssl s_client -quiet -servername %h -connect %h:443
    StrictHostKeyChecking no
    UserKnownHostsFile /dev/null
EOF

# Set proper permissions
chmod 600 ~/.ssh/config` : sshConfigTab === 'powershell' ? `$sshDir = "$env:USERPROFILE\\.ssh"
if (-not (Test-Path $sshDir)) { New-Item -ItemType Directory -Path $sshDir -Force | Out-Null }
$openssl = Get-ChildItem -Path "C:\\Program Files","C:\\Program Files (x86)","$env:LOCALAPPDATA\\Microsoft\\WinGet" -Recurse -Filter openssl.exe -ErrorAction SilentlyContinue | Select-Object -First 1 -ExpandProperty FullName
if (-not $openssl) {
    Write-Error "openssl.exe still not found anywhere - reinstall: winget install FireDaemon.OpenSSL"
} else {
    $config = "# Podstack SSH Configuration\`r\`nHost *.cloud.podstack.ai\`r\`n    ProxyCommand \`"$openssl\`" s_client -quiet -servername %h -connect %h:443\`r\`n    StrictHostKeyChecking no\`r\`n    UserKnownHostsFile /dev/null"
    Set-Content -Path "$sshDir\\config" -Encoding ascii -Value $config
    Write-Host "Wrote config using $openssl"
}` : `# Create .ssh directory if it doesn't exist
mkdir -p ~/.ssh && chmod 700 ~/.ssh

# Add the SSH config
cat >> ~/.ssh/config << 'EOF'

# Podstack SSH Configuration
Host *.cloud.podstack.ai
    ProxyCommand openssl s_client -quiet -servername %h -connect %h:443
    StrictHostKeyChecking no
    UserKnownHostsFile /dev/null
EOF

# Set proper permissions
chmod 600 ~/.ssh/config`}
                    </pre>
                    <button
                      onClick={() => {
                        const configCmd = sshConfigTab === 'mac' || sshConfigTab === 'linux' || sshConfigTab === 'gitbash'
                          ? `mkdir -p ~/.ssh && chmod 700 ~/.ssh && cat >> ~/.ssh/config << 'EOF'\n\n# Podstack SSH Configuration\nHost *.cloud.podstack.ai\n    ProxyCommand openssl s_client -quiet -servername %h -connect %h:443\n    StrictHostKeyChecking no\n    UserKnownHostsFile /dev/null\nEOF\nchmod 600 ~/.ssh/config`
                          : `$sshDir = "$env:USERPROFILE\\.ssh"
if (-not (Test-Path $sshDir)) { New-Item -ItemType Directory -Path $sshDir -Force | Out-Null }
$openssl = Get-ChildItem -Path "C:\\Program Files","C:\\Program Files (x86)","$env:LOCALAPPDATA\\Microsoft\\WinGet" -Recurse -Filter openssl.exe -ErrorAction SilentlyContinue | Select-Object -First 1 -ExpandProperty FullName
if (-not $openssl) {
    Write-Error "openssl.exe still not found anywhere - reinstall: winget install FireDaemon.OpenSSL"
} else {
    $config = "# Podstack SSH Configuration\`r\`nHost *.cloud.podstack.ai\`r\`n    ProxyCommand \`"$openssl\`" s_client -quiet -servername %h -connect %h:443\`r\`n    StrictHostKeyChecking no\`r\`n    UserKnownHostsFile /dev/null"
    Set-Content -Path "$sshDir\\config" -Encoding ascii -Value $config
    Write-Host "Wrote config using $openssl"
}`;
                        navigator.clipboard.writeText(configCmd);
                        setCopiedConfig(true);
                        toast.success('Command copied to clipboard');
                        setTimeout(() => setCopiedConfig(false), 2000);
                      }}
                      className="absolute top-2 right-2 p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded"
                      title="Copy command"
                    >
                      {copiedConfig ? (
                        <CheckIcon className="w-4 h-4 text-green-400" />
                      ) : (
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Step 2: Install OpenSSL (Windows only) */}
                {(sshConfigTab === 'powershell' || sshConfigTab === 'gitbash') && (
                  <div>
                    <h4 className="text-sm font-semibold text-ps-text mb-2 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-ps-accent text-ps-chrome text-xs flex items-center justify-center">2</span>
                      Install OpenSSL (if not installed)
                    </h4>
                    <p className="text-sm text-gray-400 mb-3">
                      OpenSSL is required for the SSH proxy. {sshConfigTab === 'gitbash' ? 'Git Bash usually includes OpenSSL.' : 'Install it (before Step 1), then locate the executable:'}
                    </p>
                    {sshConfigTab === 'powershell' && (
                      <pre className="bg-ps-page border border-ps-border text-ps-ok p-4 rounded-lg font-mono text-xs overflow-x-auto">
{`# Install FireDaemon OpenSSL (Windows 10/11)
winget install FireDaemon.OpenSSL

# winget does not add OpenSSL to PATH - locate the executable.
# Step 1's script picks this path up automatically:
Get-ChildItem "C:\\Program Files\\FireDaemon*" -Recurse -Filter openssl.exe -ErrorAction SilentlyContinue | Select-Object FullName`}
                      </pre>
                    )}
                    {sshConfigTab === 'gitbash' && (
                      <div className="bg-ps-raised rounded-lg p-3 text-sm text-gray-400">
                        Git Bash typically includes OpenSSL. If not working, ensure Git for Windows is up to date.
                      </div>
                    )}
                  </div>
                )}

                {/* Step 3: Connect */}
                <div>
                  <h4 className="text-sm font-semibold text-ps-text mb-2 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-ps-accent text-ps-chrome text-xs flex items-center justify-center">
                      {sshConfigTab === 'powershell' || sshConfigTab === 'gitbash' ? '3' : '2'}
                    </span>
                    Connect to your Pod
                  </h4>
                  <p className="text-sm text-gray-400 mb-3">
                    After adding the configuration, connect using the command below. Replace <code className="text-ps-warn">&lt;key_name&gt;</code> with your private key filename (e.g. <code className="text-ps-warn">id_rsa</code>, <code className="text-ps-warn">id_ed25519</code>):
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-ps-page border border-ps-border text-ps-ok px-4 py-3 rounded-lg font-mono text-sm">
                      {getSSHCommand()}
                    </code>
                    <button
                      onClick={() => copyToClipboard(getSSHCommand()!, 'ssh-modal')}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg"
                      title="Copy command"
                    >
                      {copiedField === 'ssh-modal' ? (
                        <CheckIcon className="w-5 h-5 text-green-400" />
                      ) : (
                        <ClipboardDocumentIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Notes */}
                <div className="bg-ps-info-bg border border-[#1F3350] rounded-lg p-3 space-y-2">
                  <p className="text-sm text-ps-info">
                    <strong>Important:</strong> Your private key file must have restricted permissions. Run this before connecting:
                  </p>
                  <code className="block bg-ps-page border border-ps-border text-ps-ok px-3 py-2 rounded font-mono text-xs">
                    chmod 400 ~/.ssh/&lt;key_name&gt;
                  </code>
                  <p className="text-sm text-ps-info">
                    <strong>Note:</strong> Make sure you have uploaded your SSH public key in the{' '}
                    <Link to="/ssh-keys" className="underline hover:text-ps-text">SSH Keys</Link> section before connecting.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 p-4 border-t border-ps-border">
              <button
                onClick={() => setShowSSHConfigModal(false)}
                className="ps-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
};

export default PodDetail;
